/**
 * Word Lists API Client with React Query Hooks
 *
 * Provides query and mutation hooks for word list management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiDelete, apiPatch } from 'services/api';
import logger from 'utils/logger';
import type {
  WordList,
  WordListListResponse,
  WordListCreateRequest,
  WordListUpdateRequest,
  WordListApiResponse,
  WordListListApiResponse,
} from '../types/wordList.types';
import { mapWordListFromApi } from '../types/wordList.types';
import type {
  WordListGenerateRequest,
  ExtendWordListRequest,
  ImportWordsFromCsvResponse,
  GenerateIdeasRequest,
  GenerateIdeasResponse,
  GenerateFromIdeaRequest,
  WordListIdea,
} from '../types/generation.types';
import { mapWordListIdeaFromApi } from '../types/generation.types';

// Query keys
export const wordListsKeys = {
  all: ['wordLists'] as const,
  lists: () => [...wordListsKeys.all, 'list'] as const,
  list: (filters: ListWordListsParams) => [...wordListsKeys.lists(), filters] as const,
  detail: (id: string) => [...wordListsKeys.all, 'detail', id] as const,
};

// ============================================================================
// API Functions
// ============================================================================

interface ListWordListsParams {
  type?: string;
  languageCode?: string;
  isPublished?: boolean;
  page?: number;
  pageSize?: number;
}

const fetchWordLists = async (params: ListWordListsParams): Promise<WordListListResponse> => {
  const queryParams: Record<string, string | number | boolean> = {};
  if (params.type) queryParams.type = params.type;
  if (params.languageCode) queryParams.language_code = params.languageCode;
  if (params.isPublished !== undefined) queryParams.is_published = params.isPublished;
  if (params.page) queryParams.page = params.page;
  if (params.pageSize) queryParams.page_size = params.pageSize;

  const response = await apiGet<WordListListApiResponse>('/api/admin/word-lists', queryParams);

  return {
    items: response.items.map(mapWordListFromApi),
    total: response.total,
    page: response.page,
    pageSize: response.page_size,
  };
};

const fetchWordList = async (id: string): Promise<WordList> => {
  const response = await apiGet<WordListApiResponse>(`/api/admin/word-lists/${id}`);
  return mapWordListFromApi(response);
};

const createWordList = async (data: WordListCreateRequest): Promise<WordList> => {
  const response = await apiPost<WordListApiResponse>('/api/admin/word-lists', data);
  return mapWordListFromApi(response);
};

const updateWordList = async (id: string, data: WordListUpdateRequest): Promise<WordList> => {
  const response = await apiPut<WordListApiResponse>(`/api/admin/word-lists/${id}`, data);
  return mapWordListFromApi(response);
};

const deleteWordList = async (id: string): Promise<void> => {
  await apiDelete<void>(`/api/admin/word-lists/${id}`);
};

const setPublished = async (id: string, isPublished: boolean): Promise<WordList> => {
  const response = await apiPatch<WordListApiResponse>(`/api/admin/word-lists/${id}/publish`, {
    is_published: isPublished,
  });
  return mapWordListFromApi(response);
};

const reorderWordLists = async (listIds: string[]): Promise<WordList[]> => {
  const response = await apiPut<WordListApiResponse[]>('/api/admin/word-lists/reorder', {
    list_ids: listIds,
  });
  return response.map(mapWordListFromApi);
};

const assignLearningGoals = async (id: string, learningGoalIds: string[]): Promise<WordList> => {
  const response = await apiPut<WordListApiResponse>(`/api/admin/word-lists/${id}/learning-goals`, {
    learning_goal_ids: learningGoalIds,
  });
  return mapWordListFromApi(response);
};

const GENERATE_WORD_LIST_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes for full list generation
const EXTEND_WORD_LIST_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes for extending list

const generateWordList = async (data: WordListGenerateRequest): Promise<WordList> => {
  const response = await apiPost<WordListApiResponse>(
    '/api/admin/word-lists/generate',
    data,
    GENERATE_WORD_LIST_TIMEOUT_MS
  );
  return mapWordListFromApi(response);
};

const extendWordList = async (id: string, data: ExtendWordListRequest): Promise<WordList> => {
  const response = await apiPost<WordListApiResponse>(
    `/api/admin/word-lists/${id}/extend`,
    data,
    EXTEND_WORD_LIST_TIMEOUT_MS
  );
  return mapWordListFromApi(response);
};

// Two-phase generation timeouts
const GENERATE_IDEAS_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes for lightweight idea generation
const GENERATE_FROM_IDEA_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes for full list generation

const generateIdeas = async (data: GenerateIdeasRequest): Promise<WordListIdea[]> => {
  const response = await apiPost<GenerateIdeasResponse>(
    '/api/admin/word-lists/generate-ideas',
    data,
    GENERATE_IDEAS_TIMEOUT_MS
  );
  return response.ideas.map(mapWordListIdeaFromApi);
};

const generateFromIdea = async (data: GenerateFromIdeaRequest): Promise<WordList> => {
  const response = await apiPost<WordListApiResponse>(
    '/api/admin/word-lists/generate-from-idea',
    data,
    GENERATE_FROM_IDEA_TIMEOUT_MS
  );
  return mapWordListFromApi(response);
};

const IMPORT_WORDS_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes for CSV import

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const importWordsFromCsv = async (id: string, file: File): Promise<ImportWordsFromCsvResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_URL}/api/admin/word-lists/${id}/import-words`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
    signal: AbortSignal.timeout(IMPORT_WORDS_TIMEOUT_MS),
  });

  const text = await response.text();

  if (!response.ok) {
    let errorMessage = 'Import failed';
    try {
      const error = JSON.parse(text);
      errorMessage = error.error?.message || error.detail || errorMessage;
    } catch {
      errorMessage = text || `HTTP ${response.status}`;
    }
    throw new Error(errorMessage);
  }

  if (!text) {
    throw new Error('Empty response from server');
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
  }
};

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Fetch word lists with filters and pagination
 */
export const useWordLists = (params: ListWordListsParams = {}) => {
  return useQuery({
    queryKey: wordListsKeys.list(params),
    queryFn: () => fetchWordLists(params),
  });
};

/**
 * Fetch a single word list by ID
 */
export const useWordList = (id: string) => {
  return useQuery({
    queryKey: wordListsKeys.detail(id),
    queryFn: () => fetchWordList(id),
    enabled: !!id,
  });
};

/**
 * Create a new word list
 */
export const useCreateWordList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: WordListCreateRequest) => createWordList(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wordListsKeys.all });
    },
    onError: (error) => {
      logger.error('Error creating word list:', error);
    },
  });
};

/**
 * Update a word list
 */
export const useUpdateWordList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: WordListUpdateRequest }) =>
      updateWordList(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: wordListsKeys.all });
      queryClient.invalidateQueries({ queryKey: wordListsKeys.detail(variables.id) });
    },
    onError: (error) => {
      logger.error('Error updating word list:', error);
    },
  });
};

/**
 * Delete a word list
 */
export const useDeleteWordList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteWordList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wordListsKeys.all });
    },
    onError: (error) => {
      logger.error('Error deleting word list:', error);
    },
  });
};

/**
 * Set publish state of a word list
 */
export const useSetPublished = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isPublished }: { id: string; isPublished: boolean }) =>
      setPublished(id, isPublished),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: wordListsKeys.all });
      queryClient.invalidateQueries({ queryKey: wordListsKeys.detail(variables.id) });
    },
    onError: (error) => {
      logger.error('Error setting publish state:', error);
    },
  });
};

/**
 * Reorder word lists
 */
export const useReorderWordLists = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (listIds: string[]) => reorderWordLists(listIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wordListsKeys.all });
    },
    onError: (error) => {
      logger.error('Error reordering word lists:', error);
    },
  });
};

/**
 * Assign learning goals to a word list
 */
export const useAssignLearningGoals = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, learningGoalIds }: { id: string; learningGoalIds: string[] }) =>
      assignLearningGoals(id, learningGoalIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: wordListsKeys.all });
      queryClient.invalidateQueries({ queryKey: wordListsKeys.detail(variables.id) });
    },
    onError: (error) => {
      logger.error('Error assigning learning goals:', error);
    },
  });
};

/**
 * Generate a word list from a prompt using AI
 *
 * Creates a complete word list with:
 * - AI-generated name and description
 * - AI-inferred type and skill level
 * - AI-generated words with example sentences and translations
 * - Generated images for each word
 */
export const useGenerateWordList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: WordListGenerateRequest) => generateWordList(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wordListsKeys.all });
    },
    onError: (error) => {
      logger.error('Error generating word list:', error);
    },
  });
};

/**
 * Extend an existing word list with more AI-generated words
 *
 * Adds new words to an existing list:
 * - Avoids duplicates with existing words
 * - Creates new words with images, pronunciations, translations
 * - Reuses existing words from the system if available
 */
export const useExtendWordList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ExtendWordListRequest }) =>
      extendWordList(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: wordListsKeys.all });
      queryClient.invalidateQueries({ queryKey: wordListsKeys.detail(variables.id) });
      // Also invalidate words in list query (using raw key to avoid circular import)
      queryClient.invalidateQueries({ queryKey: ['words', 'list', variables.id] });
    },
    onError: (error) => {
      logger.error('Error extending word list:', error);
    },
  });
};

/**
 * Generate word list ideas for admin review (Step 1 of two-phase flow)
 *
 * Generates lightweight list ideas (name, description, difficulty, sample words)
 * without full word generation. Admin reviews ideas before triggering full generation.
 */
export const useGenerateIdeas = () => {
  return useMutation({
    mutationFn: (data: GenerateIdeasRequest) => generateIdeas(data),
    onError: (error) => {
      logger.error('Error generating word list ideas:', error);
    },
  });
};

/**
 * Generate a full word list from a selected idea (Step 2 of two-phase flow)
 *
 * Takes a single idea and generates:
 * - Complete word list with all words
 * - Example sentences for each word
 * - Translations to all supported languages
 * - Images and pronunciations for new words
 */
export const useGenerateFromIdea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GenerateFromIdeaRequest) => generateFromIdea(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wordListsKeys.all });
    },
    onError: (error) => {
      logger.error('Error generating word list from idea:', error);
    },
  });
};

/**
 * Import words from a CSV file to an existing word list
 *
 * For each word in the CSV:
 * - Skips if already in the list
 * - Adds to list if word exists in the system
 * - Creates new word with full metadata and adds to list
 */
export const useImportWordsFromCsv = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => importWordsFromCsv(id, file),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: wordListsKeys.all });
      queryClient.invalidateQueries({ queryKey: wordListsKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: ['words', 'list', variables.id] });
    },
    onError: (error) => {
      logger.error('Error importing words from CSV:', error);
    },
  });
};

// ============================================================================
// Export/Import Word List Functions
// ============================================================================

/** Export schema from API (snake_case) */
interface WordListExportApiResponse {
  export_version: string;
  exported_at: string;
  word_list: {
    name: string;
    description?: string | null;
    type: string;
    language_code: string;
    skill_level_id: number;
    learning_goals: string[];
    thumbnail_url?: string | null;
  };
  words: Array<{
    word: string;
    language_code: string;
    order: number;
    translations?: Record<string, string> | null;
    pronunciations?: Record<string, { ipa?: string; audio_url?: string }> | null;
    image_url?: string | null;
    example_sentence?: string | null;
    example_translations?: Record<string, string> | null;
  }>;
}

/**
 * Export a word list to JSON file
 *
 * Triggers browser download of JSON file with complete word list data.
 */
export const exportWordList = async (listId: string): Promise<void> => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_URL}/api/admin/word-lists/${listId}/export`, {
    method: 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    const text = await response.text();
    let errorMessage = 'Export failed';
    try {
      const error = JSON.parse(text);
      errorMessage = error.error?.message || error.detail || errorMessage;
    } catch {
      errorMessage = text || `HTTP ${response.status}`;
    }
    throw new Error(errorMessage);
  }

  // Get filename from Content-Disposition header or generate one
  const contentDisposition = response.headers.get('content-disposition');
  let filename = `word-list-${listId}.json`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="([^"]+)"/);
    if (match) {
      filename = match[1];
    }
  }

  // Get the JSON data
  const data = await response.json();

  // Create blob and trigger download
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

const IMPORT_WORD_LIST_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes for word list import

/**
 * Import a word list from export JSON
 *
 * Creates a new unpublished word list with:
 * - Links to existing words or creates new ones
 * - Re-uploads all media files to local storage
 */
const importWordListFromJson = async (exportData: WordListExportApiResponse): Promise<WordList> => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_URL}/api/admin/word-lists/import`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(exportData),
    signal: AbortSignal.timeout(IMPORT_WORD_LIST_TIMEOUT_MS),
  });

  const text = await response.text();

  if (!response.ok) {
    let errorMessage = 'Import failed';
    try {
      const error = JSON.parse(text);
      errorMessage = error.error?.message || error.detail || errorMessage;
    } catch {
      errorMessage = text || `HTTP ${response.status}`;
    }
    throw new Error(errorMessage);
  }

  if (!text) {
    throw new Error('Empty response from server');
  }

  try {
    const apiResponse: WordListApiResponse = JSON.parse(text);
    return mapWordListFromApi(apiResponse);
  } catch {
    throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
  }
};

/**
 * Import a word list from a JSON file
 *
 * Reads the file, parses JSON, and sends to import endpoint.
 */
export const useImportWordList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File): Promise<WordList> => {
      const text = await file.text();
      let exportData: WordListExportApiResponse;

      try {
        exportData = JSON.parse(text);
      } catch {
        throw new Error('Invalid JSON file format');
      }

      // Basic validation
      if (!exportData.export_version || !exportData.word_list) {
        throw new Error('Invalid export file format: missing required fields');
      }

      return importWordListFromJson(exportData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wordListsKeys.all });
    },
    onError: (error) => {
      logger.error('Error importing word list:', error);
    },
  });
};
